'use client';

import { useTranslations } from 'next-intl';
import type React from 'react';
import { useEffect, useRef } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import { IconBack16 } from '@cineamo/ui/src/icons/ic-back';
import { IconCalender24 } from '@cineamo/ui/src/icons/ic-calendar';
import { IconForth16 } from '@cineamo/ui/src/icons/ic-forth';

export type ProgramDetailDateTab = {
    /** Day key (`yyyy-MM-dd`), or `null` for the "All" tab. */
    key: string | null;
    label: string;
    sublabel?: string;
    count: number;
};

type ProgramDetailDateTabsProps = {
    tabs: ProgramDetailDateTab[];
    activeKey: string | null;
    onSelect: (key: string | null) => void;
    onOpenCalendar?: () => void;
};

const TAB_BASE = 'flex-none w-[100px] h-64 rounded-4 transition-colors duration-100';
const EDGE_BUTTON =
    'hidden lg:flex absolute top-0 h-64 w-32 items-center justify-center rounded-4 cursor-pointer z-10 ' +
    'bg-[var(--surface-color)] text-[var(--on-surface-color)] hover:opacity-90';

/**
 * Horizontally scrolling date tabs plus the calendar button. The edge arrows overlap the tabs and only
 * exist while there is more to scroll in that direction (hover devices only: touch scrolls natively).
 */
export default function ProgramDetailDateTabs(props: ProgramDetailDateTabsProps): React.JSX.Element {
    const { tabs, activeKey, onSelect, onOpenCalendar } = props;
    const t = useTranslations();

    const scrollerRef = useRef<HTMLDivElement>(null);
    const { scrollState } = useScrollState({ scrollContainerRef: scrollerRef, refreshTrigger: tabs.length });

    // Keep the active tab in view while the list scrolls underneath (the scroller, not the page, moves)
    useEffect(() => {
        const scroller = scrollerRef.current;
        const tab = scroller?.querySelector<HTMLElement>('[aria-current="true"]');
        if (!scroller || !tab) {
            return;
        }
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        scroller.scrollTo({
            left: tab.offsetLeft - (scroller.clientWidth - tab.offsetWidth) / 2,
            behavior: reduceMotion ? 'auto' : 'smooth'
        });
    }, [activeKey]);

    function scrollByContainer(direction: -1 | 1) {
        const scroller = scrollerRef.current;
        scroller?.scrollTo({ left: scroller.scrollLeft + direction * scroller.clientWidth, behavior: 'smooth' });
    }

    return (
        <div className="flex flex-row gap-6">
            <div className="relative min-w-0 flex-1">
                <div
                    ref={scrollerRef}
                    className={cn(
                        'flex flex-row gap-6 overflow-x-auto no-scrollbar',
                        !scrollState.endReached &&
                            scrollState.startReached &&
                            '[mask-image:linear-gradient(to_right,black_calc(100%-32px),transparent)]',
                        scrollState.endReached &&
                            !scrollState.startReached &&
                            '[mask-image:linear-gradient(to_right,transparent,black_32px)]',
                        !scrollState.endReached &&
                            !scrollState.startReached &&
                            '[mask-image:linear-gradient(to_right,transparent,black_32px,black_calc(100%-32px),transparent)]'
                    )}>
                    {tabs.map((tab) => {
                        const isActive = tab.key === activeKey;
                        return (
                            <button
                                key={tab.key ?? 'all'}
                                type="button"
                                aria-current={isActive ? 'true' : undefined}
                                aria-label={[
                                    tab.label,
                                    tab.sublabel,
                                    t('program-detail.screenings-count', { count: tab.count })
                                ]
                                    .filter(Boolean)
                                    .join(', ')}
                                onClick={() => onSelect(tab.key)}
                                className={cn(
                                    TAB_BASE,
                                    'relative flex flex-col items-center justify-center cursor-pointer select-none',
                                    isActive
                                        ? 'bg-[var(--primary-color)] text-[var(--on-primary-color)]'
                                        : 'bg-[var(--surface-color)] text-[var(--on-surface-color)] hover:opacity-90'
                                )}>
                                <span
                                    aria-hidden
                                    className="absolute top-0 right-4 text-body-bold-10 leading-[18px] tracking-[0.5px] opacity-50">
                                    {tab.count}
                                </span>
                                <span className="text-body-bold-12 lg:text-body-bold-14 leading-[20px] lg:leading-[24px] tracking-[0.5px] lg:uppercase">
                                    {tab.label}
                                </span>
                                {tab.sublabel && (
                                    <span className="text-body-bold-10 lg:text-body-bold-12 leading-[18px] lg:leading-[20px] uppercase opacity-70 tracking-[1.6px]">
                                        {tab.sublabel}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
                {!scrollState.startReached && (
                    <button
                        type="button"
                        aria-label={t('program-detail.earlier-dates')}
                        onClick={() => scrollByContainer(-1)}
                        className={cn(EDGE_BUTTON, 'left-0')}>
                        <IconBack16 />
                    </button>
                )}
                {!scrollState.endReached && (
                    <button
                        type="button"
                        aria-label={t('program-detail.later-dates')}
                        onClick={() => scrollByContainer(1)}
                        className={cn(EDGE_BUTTON, 'right-0')}>
                        <IconForth16 />
                    </button>
                )}
            </div>
            <button
                type="button"
                aria-label={t('program.open-calendar')}
                onClick={onOpenCalendar}
                disabled={!onOpenCalendar}
                className="disabled:opacity-50 disabled:cursor-default flex-none flex items-center justify-center w-64 h-64 rounded-4 cursor-pointer bg-[var(--surface-color)] text-[var(--on-surface-color)] hover:opacity-90">
                <IconCalender24 className="w-24 h-24 lg:w-32 lg:h-32" />
            </button>
        </div>
    );
}
